'use client';

/**
 * Reusable dark mode toggle for use in topbar or in-page (e.g. `/home`, editor).
 * Syncs with document.documentElement classList and localStorage theme.
 */

import { useEffect, useState } from 'react';

import { initializeDarkMode, toggleDarkMode } from '../../lib/theme';

export type DarkModeSwitchProps = {
  /** Optional class for the button wrapper */
  className?: string;
  /** Accessible label (default: "Toggle dark mode") */
  ariaLabel?: string;
  /** 'compact' (e.g. topbar) or 'default' for in-page */
  size?: 'compact' | 'default';
  /** When true, no hover background (matches mobile look on desktop) */
  transparentHover?: boolean;
  /** Optional data-testid for tests */
  'data-testid'?: string;
};

export function DarkModeSwitch({
  className = '',
  ariaLabel = 'Toggle dark mode',
  size = 'compact',
  transparentHover = false,
  'data-testid': dataTestId = 'dark-mode-switch',
}: DarkModeSwitchProps) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    initializeDarkMode();
    setIsDarkMode(document.documentElement.classList.contains('dark'));
    setMounted(true);

    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    toggleDarkMode();
    setIsDarkMode(!isDarkMode);
  };

  if (!mounted) {
    return (
      <span
        className={className}
        style={{ display: 'inline-block', width: size === 'compact' ? 32 : 40, height: size === 'compact' ? 32 : 40 }}
        aria-hidden
      />
    );
  }

  const iconSize = size === 'compact' ? 'h-4 w-4' : 'h-5 w-5';
  const padding = size === 'compact' ? 'p-2' : 'p-2.5';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${padding} rounded-md transition-colors ${transparentHover ? 'hover:bg-transparent' : ''} ${className}`}
      style={{
        color: 'var(--topbar-foreground, var(--foreground-default))',
        ...(transparentHover ? { backgroundColor: 'transparent' } : {}),
      }}
      onMouseEnter={transparentHover ? undefined : (e) => {
        e.currentTarget.style.backgroundColor = 'var(--topbar-hover, var(--background-secondary))';
      }}
      onMouseLeave={transparentHover ? undefined : (e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {isDarkMode ? (
        <svg className={iconSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'inherit' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className={iconSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: 'inherit' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}
